import CONSTANTS from "src/utils/constants";
import { CDTSegmentType, SegmentTableDefinition } from "src/models/segment-cdt/SegmentCdtTypes";
import DateUtils from "src/utils/dateUtils";

/**
 * Display view-model for one row of the Segment Data Tables list grid.
 *
 * Kept separate from the wire type `SegmentTableDefinition` so the grid renders presentation
 * fields (formatted segment type, formatted timestamp) without leaking formatting into the
 * contract. Every field is optional-safe: the grid renders blank rather than fabricating a value
 * where the backend has none (see the FLAGs in segment-cdt-fe/specs/03-segment-tables-list-tab.md).
 */
export interface SegmentTableListRow {
    tableId: string;
    name: string;
    segmentTypeDisplay: string;
    /** From `owner` — no confirmed table-level attribute today (OQ-03b); rendered only if present. */
    owner?: string;
    /**
     * "Last Pull" has no table-level field in the contract (`last_pull_timestamp` is per-column,
     * OQ-03c). Derived from `lastUpdatedDate` for now and labelled honestly; blank when absent.
     */
    lastPullDisplay?: string;
}

/**
 * The COA dimension name for a grain — the `UI` spelling in `COA_SEGMENT_MAPPING`, which the COA endpoints key
 * off and MagicBox passes. A direct lookup: every `CDTSegmentType` member's name is also its COA key.
 *
 * Optional-chained because the argument is a wire string, not a checked enum member. This runs in the list
 * grid's row mapper, so an unknown grain — a row stored on the removed `ENTITY` grain, or an inherited
 * `Object.prototype` name — must return null rather than throw and take out the whole grid.
 */
export function segmentCoaDimension(segmentType?: CDTSegmentType | string): string | null {
    if (segmentType == null) {
        return null;
    }
    const mapping = CONSTANTS.COA_SEGMENT_MAPPING as Record<string, { UI: string } | undefined>;
    return mapping[segmentType]?.UI ?? null;
}

/**
 * The human label for a grain, from `COA_SEGMENT_MAPPING` rather than a label table of its own: a grain *is* a
 * COA dimension, so a second table could only drift from it. Falls back to the raw value for a grain the
 * mapping does not know, which is wrong-but-readable rather than blank.
 */
export function formatSegmentType(segmentType?: CDTSegmentType | string): string {
    if (segmentType == null) {
        return "";
    }
    return segmentCoaDimension(segmentType) ?? String(segmentType);
}

export function toSegmentTableListRow(def: SegmentTableDefinition): SegmentTableListRow {
    return {
        tableId: def.tableId ?? "",
        name: def.tableName ?? "",
        segmentTypeDisplay: formatSegmentType(def.segmentType),
        owner: def.owner,
        lastPullDisplay: def.lastUpdatedDate != null ? DateUtils.formatTimestamp(def.lastUpdatedDate) : undefined,
    };
}
